import { useState } from "react";
import { accountService, subscriptionService } from "@miplatform/api";
import {
  HTTP_STATUSES,
  MAX_PAGE_SIZE,
  PRINT_ORIENTATION,
} from "@miplatform/constants";
import { useComponentState, useDebounce, usePrint } from "@miplatform/hooks";
import { AccountType, IPaymentListItemResponse } from "@miplatform/types";
import { buildSearchQuery } from "@miplatform/utils";
import { useSession } from "@mibuildweb/hooks";
import { Filters } from "./PaymentListFilterComponent";

interface GetAllPaymentsResponse {
  status: number;
  data?: IPaymentListItemResponse[];
  [key: string]: any;
}

export type optionsType = { label: string; value: string };

export const usePaymentListViewModel = () => {
  const [payments, setPayments] = useState<IPaymentListItemResponse[]>([]);
  const [tenant, setTenant] = useState<any>(null);
  const [customers, setCustomers] = useState<optionsType[]>([]);
  const [bankAccountsOptions, setBankAccountsOptions] = useState<optionsType[]>(
    []
  );
  const [search, setSearch] = useState<string>("");

  const debouncedSearch = useDebounce(search, 500);
  const { handlePrintCustomJsx } = usePrint(tenant);

  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();

  const [filters, setFilters] = useState<Filters>({
    paymentStatus: undefined,
    paymentType: undefined,
    bankAccountQuickBooksId: undefined,
    accountId: "",
    groupBy: "",
  });
  const {
    session: { tenantId },
  } = useSession();

  const getCustomers = async () => {
    return stateManager.run(async () => {
      const { status, data } = await accountService.accounts.getAll({
        sort: "name",
        top: MAX_PAGE_SIZE,
        filters: {
          accountType: {
            in: [AccountType.Builder, AccountType.Service],
          },
        },
      });

      if (status === HTTP_STATUSES.OK) {
        setCustomers(
          data.map((customer: { name: string; id: string }) => ({
            label: customer.name,
            value: customer.id,
          })) || []
        );
      }
      return [];
    });
  };

  const getTenant = async () => {
    await stateManager.run(async () => {
      const response = await subscriptionService.subscriptions.get(tenantId);

      if (response.status === HTTP_STATUSES.OK) {
        setTenant(response.data);
      }
    });
  };

  const getPayments = ({
    top,
    skip,
  }: {
    top?: number;
    skip?: number;
  }): Promise<GetAllPaymentsResponse | null> => {
    return stateManager.run(async () => {
      const searchQuery = buildSearchQuery(search, [
        "bankAccountAlias",
        "paymentNumber",
        "customerAccountName",
        "creditCardReferenceNumber",
        "checkNumber",
      ]);
      const filterObject: any = {
        ...searchQuery,
      };

      if (filters.paymentStatus) {
        filterObject.remainingAmount =
          filters.paymentStatus === "FullyPaid" ? { eq: 0 } : { ne: 0 };
      }
      if (filters.paymentType) {
        filterObject.paymentType = { eq: filters.paymentType };
      }
      if (filters.accountId) {
        filterObject.customerAccountId = {
          eq: { type: "guid", value: filters.accountId },
        };
      }
      if (filters.bankAccountQuickBooksId) {
        filterObject.bankAccountQuickBooksId = {
          eq: filters.bankAccountQuickBooksId,
        };
      }
      const response: GetAllPaymentsResponse =
        await accountService.payments.getAll({
          top,
          skip,
          sort: "depositDate desc",
          enableCaching: false,
          filters: filterObject,
        });

      return response.status === HTTP_STATUSES.OK ? response : [];
    });
  };

  const handlePrint = (jsx: React.ReactNode | null) => {
    handlePrintCustomJsx(jsx, {
      orientation: PRINT_ORIENTATION.LANDSCAPE,
    });
  };

  const getBankAccounts = () => {
    return stateManager.run(async () => {
      const response = await accountService.bankAccounts.getAll({
        top: MAX_PAGE_SIZE,
      });

      if (response.status === HTTP_STATUSES.OK) {
        setBankAccountsOptions(
          response.data.map(
            (bankAccounts: {
              alias: string;
              name: string;
              id: string;
              acctNum: string;
            }) => {
              return {
                label: bankAccounts.alias || bankAccounts.name,
                value: bankAccounts.id,
              };
            }
          ) || []
        );
      }
      return [];
    });
  };

  return {
    isLoading,
    stateManager,
    search,
    debouncedSearch,
    setSearch,
    getPayments,
    setPayments,
    payments,
    customers,
    getCustomers,
    getTenant,
    handlePrint,
    bankAccountsOptions,
    getBankAccounts,
    filters,
    setFilters,
  };
};
